import { Rocket } from "lucide-react";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { customJobDefaultValues, PARSER_OPTIONS, SOURCE_TYPES, type CustomJobForm } from "~/utils/custom-job-form";
import { restoreWhenLeftEmpty } from "~/utils/form-utils";
import { Button, Card, Collapse, Form, FormItem, Input, NumberField, Select, Switch } from "~/components/ui";

type SchemaOption = { value: string; label: string };

type CustomJobFormFieldsProps = {
  control: Control<CustomJobForm>;
  schemaOptions: SchemaOption[];
  schemasLoading: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
};

export function CustomJobFormFields({
  control,
  schemaOptions,
  schemasLoading,
  isSubmitting,
  onSubmit,
}: CustomJobFormFieldsProps) {
  const { t } = useTranslation("admin");

  const advancedItems = [
    {
      key: "advanced",
      label: t("uploadAdmin.advanced.title"),
      children: (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-xs text-noa-muted block mb-1">{t("uploadAdmin.advanced.parser")}</span>
            <Controller
              name="parser"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={[...PARSER_OPTIONS]}
                  placeholder={t("uploadAdmin.form.parserPlaceholder")}
                  allowClear
                  className="w-full"
                  size="small"
                />
              )}
            />
          </div>
          <div className="flex items-center gap-2 pt-4">
            <Controller
              name="skipExisting"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.skipExisting")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.skipExisting")}</span>
          </div>
          <div className="flex items-center gap-2 pt-4">
            <Controller
              name="limitEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.maxRecords")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.maxRecords")}</span>
            <Controller
              name="maxRecords"
              control={control}
              render={({ field }) => (
                <NumberField
                  value={field.value}
                  onChange={(v) => v !== null && field.onChange(v)}
                  onBlur={restoreWhenLeftEmpty(customJobDefaultValues.maxRecords, field.onChange, field.onBlur)}
                  min={1}
                  max={10000}
                  size="small"
                  className="w-20"
                />
              )}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="lg:col-span-1 flex flex-col gap-4">
      <Card size="small">
        <Form>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <FormItem label={t("uploadAdmin.customJob.sourceType")} required className="mb-3">
              <Controller
                name="sourceType"
                control={control}
                render={({ field }) => <Select {...field} options={[...SOURCE_TYPES]} className="w-full" />}
              />
            </FormItem>

            <FormItem label={t("uploadAdmin.customJob.schema")} className="mb-3">
              <Controller
                name="schemaId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={schemaOptions}
                    loading={schemasLoading}
                    placeholder={t("uploadAdmin.customJob.schemaPlaceholder")}
                    allowClear
                    showSearch
                    className="w-full"
                  />
                )}
              />
            </FormItem>
          </div>

          <FormItem
            label={t("uploadAdmin.customJob.filesPattern")}
            tooltip={t("uploadAdmin.customJob.filesPatternTooltip")}
            className="mb-3"
          >
            <Controller
              name="filesPattern"
              control={control}
              render={({ field }) => <Input {...field} placeholder="* or path/to/files/*" />}
            />
          </FormItem>

          <FormItem className="mb-0">
            <div className="flex items-center gap-2">
              <Controller
                name="withVision"
                control={control}
                render={({ field }) => (
                  <Switch
                    size="small"
                    checked={field.value}
                    onChange={field.onChange}
                    aria-label={t("uploadAdmin.form.withVision")}
                  />
                )}
              />
              <span className="text-sm">{t("uploadAdmin.form.withVision")}</span>
            </div>
          </FormItem>
        </Form>
      </Card>

      <Collapse items={advancedItems} size="small" />

      <Button
        variant="primary"
        icon={<Rocket size={14} />}
        loading={isSubmitting}
        onClick={onSubmit}
        className="self-start"
        data-testid="custom-job-launch-button"
      >
        {t("uploadAdmin.customJob.launch")}
      </Button>
    </div>
  );
}
